import { defineStore } from 'pinia';
import { useStorage, StorageSerializers } from '@vueuse/core';
import type FlightSchool from '@/types/FlightSchool';
import type FlightSchoolCourse from '@/types/FlightSchoolCourse';
import type { FlightSchoolCourseStudent } from '@/types/FlightSchoolStudent';
import FlightSchoolRepository from '@/repositories/FlightSchoolRepository';

// Session Storage used to store the school info
const schoolInfoStorage = useStorage('flightSchoolInfo', null as FlightSchool | null, sessionStorage, { serializer: StorageSerializers.object });

export const useSchoolStore = defineStore({
    id: 'flightSchool',
    state: () => ({
        // all the schools that a user has access to
        schools: [] as FlightSchool[],

        // the current flight school
        school: null as FlightSchool|null,

        // the current course
        course: null as FlightSchoolCourse|null,

        // the students available to a school
        students: [] as FlightSchoolCourseStudent[],
    }),
    actions: {
        setSchools(schools: FlightSchool[]) {
            this.schools = schools;
        },
        setSchool(school: FlightSchool) {
            this.school = school;
            this.serialize();
        },
        serialize() {
            schoolInfoStorage.value = this.school;
        },
        deserialize() {
            this.school = this.school || schoolInfoStorage.value;
        },
        clearSchool() {
            this.school = null;
            schoolInfoStorage.value = null;
        },
        setSchoolCourses(school: FlightSchool, courses: FlightSchoolCourse[]) {
            if (school === this.school) {
                this.school.courses = courses;
                this.serialize();
            }
        },
        async setupForUserId(userId: number) {
            const schools = await FlightSchoolRepository.getFlightSchools(userId);

            const filledSchools = await Promise.all(schools.map(async (school) => ({
                ...school,
                courses: await FlightSchoolRepository.getSchoolCourses(school),
            })));

            this.setSchools(filledSchools);

            // Update the selected school with the fresh data, or remove it if it no longer exists
            const currentSchool = this.school;
            if (currentSchool !== null) {
                const freshSchool = filledSchools.find((s) => s.id === currentSchool.id);

                if (freshSchool) {
                    this.setSchool(freshSchool);
                } else {
                    this.clearSchool();
                }
            }
        },
        getSchool(schoolSlug?: string): FlightSchool|null {
            return (schoolSlug
                ? this.getSchoolByUrlSlug(schoolSlug) : null)
                ?? (this.deserialize(), this.school);
        },
        getSchoolByUrlSlug(schoolSlug: string): FlightSchool|null {
            return this.schools.find((school) => school.urlSlug === schoolSlug) ?? null;
        },
        async setStudents() {
            // assume that the school and course are set
            if (this.school && this.course) {
                const students = await FlightSchoolRepository.getCourseStudents(
                    this.school,
                    this.course,
                );
                this.students = students;
            }
        },
        async refreshStudent(student: FlightSchoolCourseStudent) {
            if (this.course === null) {
                return;
            }

            const freshStudent = await FlightSchoolRepository.getStudent(
                student.flightSchool,
                this.course,
                student.id,
                student.ratingUuid,
            );

            if (freshStudent) {
                const index = this.students.findIndex((currentStudent) => currentStudent.id === student.id);
                if (index !== -1) {
                    this.students[index] = freshStudent;
                }
            }
        },
        findStudent(id: number): FlightSchoolCourseStudent|null {
            const student = this.students.find((currentStudent) => currentStudent.id === id);
            return student ?? null;
        },
        removeSchool(id: number) {
            this.schools = this.schools.filter((school) => school.id !== id);

            if (this.school?.id === id) {
                this.clearSchool();
            }
        },
    },
});
